import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Command, SessionTemplate } from '@/types/console';
import { User, SystemSettings, AnalyticsData } from '@/types/admin';
import { mockCommands, mockTemplates } from '@/data/mockData';
import { mockUsers, mockSystemSettings, mockAnalytics } from '@/data/adminMockData';
import { queryKeys } from '@/lib/queryKeys';
import {
  getCommands,
  ApiCommand,
  updateCommandOverride,
  deleteCommandOverride,
  getTemplates,
  createTemplate as apiCreateTemplate,
  updateTemplate as apiUpdateTemplate,
  deleteTemplate as apiDeleteTemplate,
  proposeTemplate as apiProposeTemplate,
  approveTemplate as apiApproveTemplate,
  rejectTemplate as apiRejectTemplate,
  ApiTemplate
} from '@/lib/api';

interface AppStoreContextType {
  // Data
  commands: Command[];
  templates: SessionTemplate[];
  globalTemplates: SessionTemplate[];
  personalTemplates: SessionTemplate[];
  proposedTemplates: SessionTemplate[];
  allUsersTemplates: Record<string, SessionTemplate[]>;
  users: User[];
  settings: SystemSettings;
  analytics: AnalyticsData;
  commandsLoading: boolean;
  commandsError: string | null;
  templatesLoading: boolean;
  templatesError: string | null;
  // Command actions
  addCommand: (command: Omit<Command, 'id'>) => Command;
  updateCommand: (id: string, updates: Partial<Command>) => Promise<void>;
  deleteCommand: (id: string) => Promise<void>;
  refreshCommands: () => Promise<void>;
  // Template actions (updated for two-tier)
  addTemplate: (template: Omit<SessionTemplate, 'id'>, isGlobal?: boolean) => Promise<SessionTemplate>;
  updateTemplate: (id: string, updates: Partial<SessionTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  proposeTemplate: (id: string) => Promise<void>;
  approveTemplate: (id: string) => Promise<void>;
  rejectTemplate: (id: string) => Promise<void>;
  refreshTemplates: () => Promise<void>;
  // User actions
  addUser: (user: Omit<User, 'id' | 'createdAt' | 'lastActive'>) => User;
  updateUser: (id: string, updates: Partial<User>) => void;
  deleteUser: (id: string) => void;
  // Settings actions
  updateSettings: (updates: Partial<SystemSettings>) => void;
}

const AppStoreContext = createContext<AppStoreContextType | null>(null);

// Convert API command to frontend Command type
function apiCommandToCommand(apiCmd: ApiCommand): Command {
  return {
    id: apiCmd.id,
    name: apiCmd.name,
    description: apiCmd.description,
    example: apiCmd.example,
    displayName: apiCmd.displayName,
    // Extended fields for context loading
    type: apiCmd.type,
    executionType: apiCmd.executionType,
    contextPath: apiCmd.contextPath,
    toolName: apiCmd.toolName,
    clientSlug: apiCmd.clientSlug,
    estimatedTime: apiCmd.estimatedTime,
  };
}

// Convert API template to frontend SessionTemplate type
function apiTemplateToTemplate(apiTpl: ApiTemplate): SessionTemplate {
  return {
    id: apiTpl.id,
    name: apiTpl.name,
    description: apiTpl.description,
    prefillText: apiTpl.prefill_text,
    suggestedCommands: apiTpl.suggested_commands || [],
    isGlobal: apiTpl.is_global,
    userId: apiTpl.user_id,
    isProposed: apiTpl.is_proposed,
    proposedAt: apiTpl.proposed_at ? new Date(apiTpl.proposed_at) : undefined,
    ownerEmail: apiTpl.owner_email,
    createdAt: apiTpl.created_at ? new Date(apiTpl.created_at) : undefined,
    updatedAt: apiTpl.updated_at ? new Date(apiTpl.updated_at) : undefined,
  };
}

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const [commands, setCommands] = useState<Command[]>(mockCommands);
  const [commandsLoading, setCommandsLoading] = useState(false);
  const [commandsError, setCommandsError] = useState<string | null>(null);

  // Templates - two-tier system
  const [globalTemplates, setGlobalTemplates] = useState<SessionTemplate[]>(mockTemplates);
  const [personalTemplates, setPersonalTemplates] = useState<SessionTemplate[]>([]);
  const [proposedTemplates, setProposedTemplates] = useState<SessionTemplate[]>([]);
  const [allUsersTemplates, setAllUsersTemplates] = useState<Record<string, SessionTemplate[]>>({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // PERFORMANCE FIX: Memoize combined templates to avoid array creation on every render
  const templates = useMemo(
    () => [...globalTemplates, ...personalTemplates],
    [globalTemplates, personalTemplates]
  );

  const [users, setUsers] = useState<User[]>(mockUsers);
  const [settings, setSettings] = useState<SystemSettings>(mockSystemSettings);
  const [analytics] = useState<AnalyticsData>(mockAnalytics);

  // Load commands from API (checks React Query cache first from bootstrap)
  const refreshCommands = useCallback(async (forceRefresh = false) => {
    // Check if bootstrap already seeded commands in cache
    if (!forceRefresh) {
      const cachedCommands = queryClient.getQueryData<Command[]>(queryKeys.commands.list());
      if (cachedCommands && cachedCommands.length > 0) {
        console.log(`[AppStore] Using ${cachedCommands.length} cached commands from bootstrap`);
        setCommands(cachedCommands);
        return;
      }
    }

    setCommandsLoading(true);
    setCommandsError(null);
    try {
      const response = await getCommands();
      const frontendCommands = response.commands.map(apiCommandToCommand);
      setCommands(frontendCommands);
      // Update cache for consistency
      queryClient.setQueryData(queryKeys.commands.list(), frontendCommands);
      console.log(`[AppStore] Loaded ${frontendCommands.length} commands from API`);
    } catch (error) {
      console.warn('Failed to load commands from API, using mock data:', error);
      setCommandsError(error instanceof Error ? error.message : 'Failed to load commands');
      // Keep mock commands as fallback
    } finally {
      setCommandsLoading(false);
    }
  }, [queryClient]);

  // Load templates from API (checks React Query cache first from bootstrap)
  const refreshTemplates = useCallback(async (forceRefresh = false) => {
    // Check if bootstrap already seeded templates in cache
    if (!forceRefresh) {
      const cachedTemplates = queryClient.getQueryData<SessionTemplate[]>(queryKeys.templates.list());
      if (cachedTemplates && cachedTemplates.length > 0) {
        console.log(`[AppStore] Using ${cachedTemplates.length} cached templates from bootstrap`);
        // Bootstrap only returns approved templates, set as global
        setGlobalTemplates(cachedTemplates);
        return;
      }
    }

    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const response = await getTemplates();

      // Update global templates
      setGlobalTemplates(response.global_templates.map(apiTemplateToTemplate));

      // Update personal templates
      setPersonalTemplates(response.personal_templates.map(apiTemplateToTemplate));

      // Update proposed templates (admin only)
      if (response.proposed_templates) {
        setProposedTemplates(response.proposed_templates.map(apiTemplateToTemplate));
      }

      // Update all users' templates (admin only)
      if (response.all_users_templates) {
        const converted: Record<string, SessionTemplate[]> = {};
        for (const [email, tpls] of Object.entries(response.all_users_templates)) {
          converted[email] = tpls.map(apiTemplateToTemplate);
        }
        setAllUsersTemplates(converted);
      }

      console.log(`[AppStore] Loaded templates: ${response.global_templates.length} global, ${response.personal_templates.length} personal`);
    } catch (error) {
      console.warn('Failed to load templates from API, using mock data:', error);
      setTemplatesError(error instanceof Error ? error.message : 'Failed to load templates');
      // Keep mock templates as fallback
    } finally {
      setTemplatesLoading(false);
    }
  }, [queryClient]);

  // PERFORMANCE FIX: Check React Query cache first (seeded by BootstrapProvider)
  // Only make API calls if cache is empty
  useEffect(() => {
    // Small delay to allow BootstrapProvider to seed the cache first
    const timer = setTimeout(() => {
      Promise.all([refreshCommands(), refreshTemplates()]);
    }, 50);
    return () => clearTimeout(timer);
  }, [refreshCommands, refreshTemplates]);

  // Commands CRUD
  const addCommand = useCallback((command: Omit<Command, 'id'>) => {
    const newCommand: Command = {
      ...command,
      id: `cmd-${Date.now()}`,
    };
    setCommands(prev => [...prev, newCommand]);
    return newCommand;
  }, []);

  const updateCommand = useCallback(async (id: string, updates: Partial<Command>) => {
    try {
      // Call API to save override
      await updateCommandOverride(id, {
        displayName: updates.displayName,
        description: updates.description,
        example: updates.example,
      });
      // Update local state optimistically
      setCommands(prev =>
        prev.map(cmd => (cmd.id === id ? { ...cmd, ...updates } : cmd))
      );
    } catch (error) {
      console.error('Failed to update command:', error);
      throw error;
    }
  }, []);

  const deleteCommand = useCallback(async (id: string) => {
    try {
      // Call API to delete override (resets to defaults)
      await deleteCommandOverride(id);
      // Refresh commands to get the default values back
      await refreshCommands();
    } catch (error) {
      console.error('Failed to delete command override:', error);
      throw error;
    }
  }, [refreshCommands]);

  // Templates CRUD (two-tier system)
  const addTemplate = useCallback(async (template: Omit<SessionTemplate, 'id'>, isGlobal = false): Promise<SessionTemplate> => {
    try {
      const response = await apiCreateTemplate({
        name: template.name,
        description: template.description,
        prefill_text: template.prefillText,
        suggested_commands: template.suggestedCommands,
        is_global: isGlobal,
      });

      const newTemplate = apiTemplateToTemplate(response);

      // Update local state
      if (newTemplate.isGlobal) {
        setGlobalTemplates(prev => [...prev, newTemplate]);
      } else {
        setPersonalTemplates(prev => [...prev, newTemplate]);
      }

      return newTemplate;
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  }, []);

  const updateTemplate = useCallback(async (id: string, updates: Partial<SessionTemplate>) => {
    try {
      await apiUpdateTemplate(id, {
        name: updates.name,
        description: updates.description,
        prefill_text: updates.prefillText,
        suggested_commands: updates.suggestedCommands,
      });

      // Update local state in both lists
      const updateFn = (prev: SessionTemplate[]) =>
        prev.map(tpl => (tpl.id === id ? { ...tpl, ...updates } : tpl));

      setGlobalTemplates(updateFn);
      setPersonalTemplates(updateFn);
    } catch (error) {
      console.error('Failed to update template:', error);
      throw error;
    }
  }, []);

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      await apiDeleteTemplate(id);

      // Remove from both lists
      setGlobalTemplates(prev => prev.filter(tpl => tpl.id !== id));
      setPersonalTemplates(prev => prev.filter(tpl => tpl.id !== id));
      setProposedTemplates(prev => prev.filter(tpl => tpl.id !== id));
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  }, []);

  const proposeTemplate = useCallback(async (id: string) => {
    try {
      await apiProposeTemplate(id);
      // Refresh to get updated state
      await refreshTemplates();
    } catch (error) {
      console.error('Failed to propose template:', error);
      throw error;
    }
  }, [refreshTemplates]);

  const approveTemplate = useCallback(async (id: string) => {
    try {
      await apiApproveTemplate(id);
      // Refresh to get updated state
      await refreshTemplates();
    } catch (error) {
      console.error('Failed to approve template:', error);
      throw error;
    }
  }, [refreshTemplates]);

  const rejectTemplate = useCallback(async (id: string) => {
    try {
      await apiRejectTemplate(id);
      // Refresh to get updated state
      await refreshTemplates();
    } catch (error) {
      console.error('Failed to reject template:', error);
      throw error;
    }
  }, [refreshTemplates]);

  // Users CRUD
  const addUser = useCallback((user: Omit<User, 'id' | 'createdAt' | 'lastActive'>) => {
    const newUser: User = {
      ...user,
      id: `usr-${Date.now()}`,
      createdAt: new Date(),
      lastActive: new Date(),
    };
    setUsers(prev => [...prev, newUser]);
    return newUser;
  }, []);

  const updateUser = useCallback((id: string, updates: Partial<User>) => {
    setUsers(prev =>
      prev.map(user => (user.id === id ? { ...user, ...updates } : user))
    );
  }, []);

  const deleteUser = useCallback((id: string) => {
    setUsers(prev => prev.filter(user => user.id !== id));
  }, []);

  // Settings
  const updateSettings = useCallback((updates: Partial<SystemSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    commands,
    templates,
    globalTemplates,
    personalTemplates,
    proposedTemplates,
    allUsersTemplates,
    users,
    settings,
    analytics,
    commandsLoading,
    commandsError,
    templatesLoading,
    templatesError,
    addCommand,
    updateCommand,
    deleteCommand,
    refreshCommands,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    proposeTemplate,
    approveTemplate,
    rejectTemplate,
    refreshTemplates,
    addUser,
    updateUser,
    deleteUser,
    updateSettings,
  }), [
    commands,
    templates,
    globalTemplates,
    personalTemplates,
    proposedTemplates,
    allUsersTemplates,
    users,
    settings,
    analytics,
    commandsLoading,
    commandsError,
    templatesLoading,
    templatesError,
    addCommand,
    updateCommand,
    deleteCommand,
    refreshCommands,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    proposeTemplate,
    approveTemplate,
    rejectTemplate,
    refreshTemplates,
    addUser,
    updateUser,
    deleteUser,
    updateSettings,
  ]);

  return (
    <AppStoreContext.Provider value={value}>
      {children}
    </AppStoreContext.Provider>
  );
}

export function useAppStore() {
  const context = useContext(AppStoreContext);
  if (!context) {
    throw new Error('useAppStore must be used within AppStoreProvider');
  }
  return context;
}
